import dayjs from "dayjs"
import { useMemo } from "react"
import { useFollowedUsers } from "src/contexts"
import {
  useThisMinute,
  useOldestUserEntry,
  useOldestUserEvent,
} from "src/hooks"
import { oldestDate } from "src/utils"

export const graphEndPadding = 45

export function useGraphBounds(pointsPerHour: number): [string, string] {
  const thisMinute = useThisMinute()

  // add padding to the end of the graph
  const graphEnd = useMemo(
    () => {
      const hours = graphEndPadding / pointsPerHour
      return dayjs(thisMinute).add(
        hours,
        "hours",
      )
        .toISOString()
    },
    [
      thisMinute,
      pointsPerHour,
    ],
  )

  const today = useMemo(
    () => dayjs(graphEnd).startOf("day")
      .subtract(
        0,
        "days",
      )
      .toISOString(),
    [graphEnd],
  )

  const ninetyDaysAgo = useMemo(
    () => dayjs(graphEnd).startOf("day")
      .subtract(
        90,
        "days",
      )
      .toISOString(),
    [graphEnd],
  )

  const { currentUserId } = useFollowedUsers()
  const oldestEvent = useOldestUserEvent(currentUserId ?? -1)
  const oldestEntry = useOldestUserEntry(currentUserId ?? -1)

  /*
   * if there is no user data, return today
   * if there is less than 90 days of user data, return the oldest user data started_at
   * if there is more than 90 days of user data, return 90 days
   */
  const graphStart = useMemo(
    () => {
      const oldestUserData = oldestDate(
        oldestEvent?.started_at || today,
        oldestEntry?.started_at || today,
      )

      if (dayjs(oldestUserData).isBefore(ninetyDaysAgo)) {
        return ninetyDaysAgo
      }

      const diffInWholeDays = Math.ceil(
        dayjs(today).startOf("day")
          .diff(
            oldestUserData,
            "days",
            true,
          ),
      )

      return dayjs(today)
        .startOf("day")
        .subtract(
          diffInWholeDays,
          "days",
        )
        .toISOString()
    },
    [
      today,
      ninetyDaysAgo,
      oldestEvent,
      oldestEntry,
    ],
  )

  return [
    graphStart,
    graphEnd,
  ]
}
